import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Users, BookOpen, Headphones, Mail, Download, Globe, Zap, Bot, TrendingUp, Settings, BarChart3, Lightbulb, ChevronRight } from 'lucide-react';
import FunnelTemplateLibrary from './components/FunnelTemplateLibrary';
import AIFunnelBuilder from './components/AIFunnelBuilder';
import FunnelAnalytics from './components/FunnelAnalytics';
import ContentGenerator from './components/ContentGenerator';
import AutomationPanel from './components/AutomationPanel';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import Icon from '../../components/AppIcon';
import PageLayout from '../../components/ui/PageLayout';


const AIBuildMarketingFunnels = () => {
  const [activeTab, setActiveTab] = useState('builder');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [funnelData, setFunnelData] = useState(null);

  useEffect(() => {
    // Load any existing funnel data or initialize
    const savedFunnel = localStorage.getItem('currentFunnel');
    if (savedFunnel) {
      setFunnelData(JSON.parse(savedFunnel));
    }
  }, []);

  const funnelCategories = [
    {
      id: 'online-courses',
      title: 'Online Courses',
      description: 'Create, market and sell your expertise',
      icon: BookOpen,
      color: 'bg-blue-500',
      features: [
        'Course enrollment funnels',
        'Student engagement sequences',
        'Upsell automation',
        'Community integration'
      ]
    },
    {
      id: 'coaching',
      title: 'Coaching',
      description: 'Add more value to your audience with our full suite of coaching tools',
      icon: Users,
      color: 'bg-green-500',
      features: [
        'Consultation booking funnels',
        'Client onboarding sequences',
        'Progress tracking automation',
        'Renewal campaigns'
      ]
    },
    {
      id: 'podcasts',
      title: 'Podcasts',
      description: 'Create and launch your podcast',
      icon: Headphones,
      color: 'bg-purple-500',
      features: [
        'Subscriber acquisition funnels',
        'Episode promotion sequences',
        'Sponsor integration automation',
        'Listener engagement campaigns'
      ]
    },
    {
      id: 'newsletters',
      title: 'Newsletters',
      description: 'Boost engagement and revenue with AI-powered newsletters',
      icon: Mail,
      color: 'bg-orange-500',
      features: [
        'Newsletter signup funnels',
        'Content distribution automation',
        'Subscriber segmentation',
        'Monetization sequences'
      ]
    },
    {
      id: 'digital-downloads',
      title: 'Digital Downloads',
      description: 'Enjoy unlimited downloads',
      icon: Download,
      color: 'bg-indigo-500',
      features: [
        'Product sales funnels',
        'Download delivery automation',
        'Customer retention campaigns',
        'Cross-sell sequences'
      ]
    },
    {
      id: 'landing-pages',
      title: 'Landing Pages',
      description: 'Build high-converting landing pages without needing a designer',
      icon: Globe,
      color: 'bg-pink-500',
      features: [
        'High-converting page templates',
        'A/B testing automation',
        'Lead capture optimization',
        'Conversion tracking'
      ]
    },
    {
      id: 'funnels-automations',
      title: 'Funnels & Automations',
      description: 'Automate and scale your campaigns with ready-made funnels',
      icon: Zap,
      color: 'bg-yellow-500',
      features: [
        'Multi-channel automation',
        'Behavioral triggers',
        'Advanced segmentation',
        'Performance optimization'
      ]
    },
    {
      id: 'no-code-ai-web-builder',
      title: 'No-Code AI Web Builder',
      description: 'Create a fully integrated website with a few clicks',
      icon: Bot,
      color: 'bg-teal-500',
      features: [
        'AI-powered website generation',
        'Drag-and-drop builder',
        'Mobile-responsive designs',
        'SEO optimization'
      ]
    }
  ];

  const tabs = [
    { id: 'builder', label: 'AI Funnel Builder', icon: Bot },
    { id: 'templates', label: 'Template Library', icon: BookOpen },
    { id: 'content', label: 'Content Generator', icon: Lightbulb },
    { id: 'automation', label: 'Automation', icon: Zap },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'optimizer', label: 'Performance', icon: TrendingUp }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'builder':
        return (
          <AIFunnelBuilder 
            selectedCategory={selectedTemplate}
            onFunnelCreate={setFunnelData}
            existingFunnel={funnelData}
          />
        );
      case 'templates':
        return (
          <FunnelTemplateLibrary 
            categories={funnelCategories}
            onTemplateSelect={setSelectedTemplate}
            selectedTemplate={selectedTemplate}
          />
        );
      case 'content':
        return <ContentGenerator funnelData={funnelData} />;
      case 'automation':
        return <AutomationPanel funnelData={funnelData} />;
      case 'analytics':
        return <FunnelAnalytics funnelData={funnelData} />;
      case 'optimizer':
        return <PerformanceOptimizer funnelData={funnelData} />;
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Marketing Funnel Builder</h1>
          <p className="text-muted-foreground">
            Build and optimize marketing funnels with AI-powered insights and automation
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            <Bot className="w-4 h-4" />
          </button>
          <button className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors">
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 rounded-full">
            <Bot className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          AI Build Marketing Funnels
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          Transform your business with AI-powered marketing funnels. Build, launch, and optimize 
          high-converting funnels for courses, coaching, podcasts, newsletters, and more.
        </p>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {[
            { label: 'Funnel Templates', value: '50+', icon: Target },
            { label: 'Conversion Rate', value: '45%', icon: TrendingUp },
            { label: 'Automation Rules', value: '100+', icon: Zap },
            { label: 'Integration Options', value: '25+', icon: Settings }
          ]?.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-center mb-2">
                <stat.icon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat?.value}</div>
              <div className="text-sm text-gray-600">{stat?.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
      {/* Main Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-8">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-purple-500 text-purple-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {renderTabContent()}
      </motion.div>
      {/* Quick Access Categories - Only show on builder tab */}
      {activeTab === 'builder' && !selectedTemplate && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Choose Your Funnel Type
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {funnelCategories?.map((category) => {
              const Icon = category?.icon;
              return (
                <motion.div
                  key={category?.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedTemplate(category)}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg ${category?.color}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {category?.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {category?.description}
                  </p>
                  <div className="space-y-1">
                    {category?.features?.slice(0, 2)?.map((feature, index) => (
                      <div key={index} className="text-xs text-gray-500 flex items-center">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}
    </PageLayout>
  );
};

export default AIBuildMarketingFunnels;